import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Merge, Undo2, X } from "lucide-react";
import { ListenButton } from "../components/ListenButton.tsx";
import type { StationDupCluster, StationDupGroup, StationDuplicatesResult } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useToast } from "../lib/toast.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { CountryPicker, Flag } from "../components/CountryPicker.tsx";
import { Button, Card, Chips, ConfirmButton, Empty, ErrorState } from "../components/Ui.tsx";

/**
 * Olası kopya istasyonlar: aynı ülkede adı gevşekçe aynı ("BBC Radio 4 (128k)" /
 * "BBC Radio 4", "Radio Eska Wrocław" / "Eska Wrocław") ama ayrı istasyon olarak
 * duran gruplar. Birleştir seçilen grupların kaynaklarına aynı elle grubu
 * yazar; Ayrı kalsın kümeyi listeden düşürür (kümeye yeni istasyon katılırsa
 * yeniden gelir). Farklı şehirlerin aynı adlı radyoları da eşleşebilir, karar
 * burada verilir.
 */
const PAGE = 30;
const VIEWS = ["open", "dismissed"] as const;
type View = (typeof VIEWS)[number];

export function StationDuplicatesPage(): ReactNode {
  const toast = useToast();
  const queryClient = useQueryClient();
  // Filtreler adreste: bağlantıyla paylaşılır, geri tuşu çalışır.
  const [params, setParams] = useSearchParams();
  const country = params.get("country") ?? "";
  const view: View = params.get("view") === "dismissed" ? "dismissed" : "open";
  const page = Math.max(0, Number(params.get("page") ?? 0) || 0);
  const q = params.get("q") ?? "";
  const [search, setSearch] = useState(q);

  const update = (patch: Record<string, string | null>, resetPage = true): void => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === "") next.delete(k);
      else next.set(k, v);
    }
    if (resetPage && !("page" in patch)) next.delete("page");
    setParams(next, { replace: "q" in patch });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== q) update({ q: search.trim() });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Otomatik birleştirme arka planda: bitene (sayı sıfırlanana) kadar liste sık yenilenir, en fazla 5 dk.
  const [autoSince, setAutoSince] = useState<number | null>(null);
  const autoActive = autoSince !== null && Date.now() - autoSince < 5 * 60_000;

  const list = useQuery({
    refetchInterval: autoActive ? 5000 : false,
    queryKey: ["station-duplicates", country, view, q, page],
    queryFn: () =>
      client.stationDuplicates({
        country: country || undefined,
        q: q || undefined,
        dismissed: view === "dismissed",
        limit: PAGE,
        offset: page * PAGE,
      }),
    placeholderData: keepPreviousData,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["station-duplicates"] });
    void queryClient.invalidateQueries({ queryKey: ["stations"] });
  };

  const merge = useMutation({
    mutationFn: ({ groups }: { cluster: StationDupCluster; groups: StationDupGroup[] }) =>
      client.mergeStationDuplicates(groups.map((g) => g.key)),
    onSuccess: (res, { groups }) => {
      invalidate();
      // Kaynak = yayın adresi; bir istasyonun birden çok kaynağı olabilir, sayılar ayrı yazılır.
      const summary = `${groups.length} istasyon tek oldu · toplam ${res.sources} kaynak`;
      toast({
        tone: res.firestore.ok === false ? "danger" : "ok",
        title: `${groups[0]!.name} birleştirildi`,
        body:
          res.firestore.ok === false
            ? `${summary}; TR listesine yansıtılamadı: ${res.firestore.error ?? ""}`
            : summary,
      });
    },
    onError: (err) => toast({ tone: "danger", title: "Birleştirilemedi", body: humanizeError(err) }),
  });

  useEffect(() => {
    if (autoSince !== null && (list.data?.autoMergeable === 0 || !autoActive)) {
      setAutoSince(null);
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data?.autoMergeable, autoActive]);

  const autoMerge = useMutation({
    mutationFn: () => client.autoMergeStationDuplicates(),
    onSuccess: (res) => {
      setAutoSince(Date.now());
      toast({
        tone: "ok",
        title: res.started ? "Kesin kopyalar birleştiriliyor" : "Birleştirme zaten çalışıyor",
        body: "arka planda sürer; liste kendiliğinden yenilenir",
      });
    },
    onError: (err) => toast({ tone: "danger", title: "Başlatılamadı", body: humanizeError(err) }),
  });

  const dismiss = useMutation({
    mutationFn: ({ cluster, dismissed }: { cluster: StationDupCluster; dismissed: boolean }) =>
      client.dismissStationDuplicates(
        cluster.groups.map((g) => g.key),
        dismissed,
      ),
    onSuccess: (_res, { cluster, dismissed }) => {
      invalidate();
      toast({
        tone: "ok",
        title: dismissed ? `${cluster.groups[0]!.name}: ayrı kalacak` : `${cluster.groups[0]!.name} yeniden önerilecek`,
      });
    },
    onError: (err) => toast({ tone: "danger", title: "Kaydedilemedi", body: humanizeError(err) }),
  });

  const total = list.data?.total ?? 0;
  const pages = Math.ceil(total / PAGE);
  const from = page * PAGE + 1;
  const to = Math.min(total, (page + 1) * PAGE);
  const busyId = merge.isPending
    ? merge.variables?.cluster.id
    : dismiss.isPending
      ? dismiss.variables?.cluster.id
      : undefined;

  return (
    <>
      <PageHeader
        kicker="raadiocast"
        title="Olası kopyalar"
        meta="Aynı ülkede adı neredeyse aynı olan, ayrı istasyon görünen kayıtlar. Birleştir seçilenleri tek istasyon yapar."
      />

      <PageBody>
        {view === "open" && (autoActive || !!list.data?.autoMergeable || !!list.data?.lastAutoMerge) && (
          <Card flush>
            {(autoActive || !!list.data?.autoMergeable) && (
              <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                <p className="min-w-0 flex-1 basis-[260px] text-xs text-neutral-400">
                  {autoActive
                    ? `Kesin kopyalar birleştiriliyor… kalan ${(list.data?.autoMergeable ?? 0).toLocaleString("tr-TR")} küme`
                    : `${(list.data?.autoMergeable ?? 0).toLocaleString("tr-TR")} kümede adı neredeyse aynı istasyonlar ya aynı yayın adresini kullanıyor ya da adları yalnızca kalite/biçim etiketiyle (320k, AAC, HLS…) ayrılıyor; bunlar onaysız birleştirilebilir. TR listesindekiler ve "ayrı kalsın" dediklerin dahil değil. Her gece senkrondan sonra kendiliğinden de çalışır.`}
                </p>
                <ConfirmButton
                  size="sm"
                  variant="primary"
                  icon={Merge}
                  confirmLabel="Hepsi birleşsin mi?"
                  loading={autoMerge.isPending || autoActive}
                  onConfirm={() => autoMerge.mutate()}
                >
                  Kesin kopyaları birleştir ({(list.data?.autoMergeable ?? 0).toLocaleString("tr-TR")})
                </ConfirmButton>
              </div>
            )}
            {list.data?.lastAutoMerge && <LastAutoMergeLine last={list.data.lastAutoMerge} />}
          </Card>
        )}

        <Card
          title={list.data ? `${total.toLocaleString("tr-TR")} küme` : "Kümeler"}
          hint="dinlenmeye göre sıralı"
          action={
            <>
              <Chips
                aria-label="Görünüm"
                values={VIEWS}
                value={view}
                labels={{ open: "Karar bekleyen", dismissed: "Ayrı kalanlar" }}
                onChange={(v) => update({ view: v === "open" ? null : v })}
              />
              <CountryPicker
                value={country}
                onChange={(c) => update({ country: c })}
                options={list.data?.countries ?? []}
              />
              <input
                type="search"
                className="input w-full sm:!w-56"
                aria-label="İstasyon adı ara"
                placeholder="istasyon adı ara"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </>
          }
          flush
        >
          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : list.isLoading ? (
            <Empty>Yükleniyor… (ilk açılışta tüm dizin taranır, birkaç saniye sürer)</Empty>
          ) : list.data?.clusters.length ? (
            <>
              <ul>
                {list.data.clusters.map((c) => (
                  <li key={c.id}>
                    <ClusterRow
                      cluster={c}
                      busy={busyId === c.id}
                      disabled={merge.isPending || dismiss.isPending}
                      onMerge={(groups) => merge.mutate({ cluster: c, groups })}
                      onDismiss={(dismissed) => dismiss.mutate({ cluster: c, dismissed })}
                    />
                  </li>
                ))}
              </ul>
              {pages > 1 && (
                <nav aria-label="Sayfalama" className="row items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Önceki sayfa"
                    disabled={page === 0}
                    onClick={() => update({ page: String(page - 1) }, false)}
                  >
                    <span aria-hidden>←</span> Önceki
                  </Button>
                  <span className="mono text-2xs text-neutral-600">
                    {from.toLocaleString("tr-TR")}–{to.toLocaleString("tr-TR")} / {total.toLocaleString("tr-TR")}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Sonraki sayfa"
                    disabled={page + 1 >= pages}
                    onClick={() => update({ page: String(page + 1) }, false)}
                  >
                    Sonraki <span aria-hidden>→</span>
                  </Button>
                </nav>
              )}
            </>
          ) : (
            <Empty>
              {q
                ? `“${q}” ile eşleşen küme yok`
                : view === "dismissed"
                  ? "Ayrı kalsın denen küme yok"
                  : "Karar bekleyen olası kopya yok"}
            </Empty>
          )}
        </Card>
      </PageBody>
    </>
  );
}

/** Son otomatik birleştirmenin özeti: ne zaman, ne kadar, gece mi elle mi. */
function LastAutoMergeLine({ last }: { last: NonNullable<StationDuplicatesResult["lastAutoMerge"]> }): ReactNode {
  const when = new Date(last.at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
  const how = last.nightly ? "gece senkronundan sonra kendiliğinden" : "konsoldan elle";
  return (
    <p
      role="status"
      className="flex flex-wrap items-center gap-2 border-t border-neutral-800 px-4 py-2.5 text-2xs text-neutral-500 first:border-t-0"
    >
      <span className="text-neutral-400">Son otomatik birleştirme:</span>
      <span>{when}</span>
      <span>·</span>
      <span>{how}</span>
      <span>·</span>
      {last.status === "ok" ? (
        <span>
          {(last.merged ?? 0).toLocaleString("tr-TR")} istasyon, {(last.groups ?? 0).toLocaleString("tr-TR")} grup birleşti
        </span>
      ) : (
        <span className="text-danger">hata: {last.error ?? "bilinmiyor"}</span>
      )}
    </p>
  );
}

function ClusterRow({
  cluster,
  busy,
  disabled,
  onMerge,
  onDismiss,
}: {
  cluster: StationDupCluster;
  busy: boolean;
  disabled: boolean;
  onMerge: (groups: StationDupGroup[]) => void;
  onDismiss: (dismissed: boolean) => void;
}): ReactNode {
  // Varsayılan hepsi seçili; "Kent FM / Radyo Kent / Kent Radyo" gibi kümede
  // yalnızca aynı olanlar seçilip birleştirilir, kalan küme yeniden gelir.
  const [picked, setPicked] = useState<Set<string>>(() => new Set(cluster.groups.map((g) => g.key)));
  useEffect(() => setPicked(new Set(cluster.groups.map((g) => g.key))), [cluster.id]);
  const chosen = cluster.groups.filter((g) => picked.has(g.key));
  const curatedCount = chosen.filter((g) => g.curated).length;
  const toggle = (key: string, on: boolean): void =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  return (
    <div className="grid gap-2 border-b border-neutral-800 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2 text-2xs text-neutral-500">
        <Flag code={cluster.country} />
        <span className="mono">{cluster.country}</span>
        <span>·</span>
        <span>{cluster.groups.length} istasyon</span>
        <span>·</span>
        <span className="mono">{cluster.clicks.toLocaleString("tr-TR")} tık</span>
        {cluster.groups.some((g) => g.curated) && (
          <span
            className="tag tag-accent"
            title="Uygulamanın TR listesindeki kayıtlar birleşince teke iner; diğerleri yedek kaynak olur"
          >
            TR listesinde
          </span>
        )}
      </div>

      <ul className="grid gap-1.5">
        {cluster.groups.map((g) => (
          <li key={g.key}>
            <GroupLine
              group={g}
              country={cluster.country}
              checked={picked.has(g.key)}
              selectable={!cluster.dismissed}
              onToggle={(on) => toggle(g.key, on)}
            />
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        {cluster.dismissed ? (
          <Button size="sm" variant="ghost" icon={Undo2} loading={busy} disabled={disabled} onClick={() => onDismiss(false)}>
            Yeniden öner
          </Button>
        ) : (
          <>
            {curatedCount >= 2 ? (
              <ConfirmButton
                size="sm"
                variant="primary"
                icon={Merge}
                confirmLabel={`TR listesindeki ${curatedCount} kayıt teke inecek, emin misin?`}
                loading={busy}
                disabled={disabled || chosen.length < 2}
                onConfirm={() => onMerge(chosen)}
              >
                Birleştir ({chosen.length})
              </ConfirmButton>
            ) : (
              <Button
                size="sm"
                variant="primary"
                icon={Merge}
                loading={busy}
                disabled={disabled || chosen.length < 2}
                title={chosen.length < 2 ? "En az iki istasyon seç" : undefined}
                onClick={() => onMerge(chosen)}
              >
                Birleştir ({chosen.length})
              </Button>
            )}
            <Button size="sm" variant="ghost" icon={X} disabled={disabled} onClick={() => onDismiss(true)}>
              Ayrı kalsın
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function GroupLine({
  group,
  country,
  checked,
  selectable,
  onToggle,
}: {
  group: StationDupGroup;
  country: string;
  checked: boolean;
  selectable: boolean;
  onToggle: (on: boolean) => void;
}): ReactNode {
  const link = `/stations?${new URLSearchParams({ country, q: group.name }).toString()}`;
  return (
    <label className={`flex items-center gap-3 ${selectable ? "cursor-pointer" : ""}`}>
      {selectable && (
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={`${group.name} birleştirilsin`}
          className="size-3.5 shrink-0 accent-[var(--color-accent)]"
        />
      )}
      {group.favicon ? (
        <img
          src={group.favicon}
          alt=""
          aria-hidden
          loading="lazy"
          className="size-8 shrink-0 rounded-[6px] bg-neutral-900 object-contain"
          onError={(e) => {
            e.currentTarget.style.visibility = "hidden";
          }}
        />
      ) : (
        <span className="size-8 shrink-0 rounded-[6px] border border-dashed border-neutral-700" aria-hidden />
      )}
      <span className="grid min-w-0 flex-1 gap-0.5">
        <span className="flex flex-wrap items-center gap-2 text-neutral-100">
          <Link to={link} className="hover:underline" title="İstasyonlar sayfasında aç" onClick={(e) => e.stopPropagation()}>
            {group.name}
          </Link>
          {group.curated && <span className="tag tag-accent">TR listesinde</span>}
          {group.manual && (
            <span className="tag tag-outline" title="Kaynaklarından birinde elle grup var; birleşince bu grup adı korunur">
              elle grup
            </span>
          )}
        </span>
        {group.otherNames.length > 0 && (
          <span className="truncate text-2xs text-neutral-500" title={group.otherNames.join(" · ")}>
            ayrıca: {group.otherNames.join(" · ")}
          </span>
        )}
      </span>
      <span className="grid shrink-0 justify-items-end gap-0.5 text-2xs sm:flex sm:items-center sm:gap-3">
        <span className="text-neutral-500" title={`${group.sources} kaynak, ${group.visible} tanesi görünür`}>
          {group.visible}/{group.sources} kaynak
        </span>
        <span className="mono text-neutral-600 sm:w-16 sm:text-right" title="radio-browser tıklama sayısı">
          {group.clicks.toLocaleString("tr-TR")} tık
        </span>
      </span>
      {group.url ? (
        <ListenButton url={group.url} name={group.name} compact />
      ) : (
        <span className="w-[58px] shrink-0" aria-hidden />
      )}
    </label>
  );
}
